import {groupByUniqueKey} from '@benchling/js-utils';

import * as analytics from 'analytics/Analytics';
import {CodonTranslationTable} from 'biojs/BioConstants';
import {Sequence} from 'components/Sequence';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import {CustomField, Strand} from 'constants/MolbioConstants';
import {FeaturePanelState, SequenceFileState} from 'FileStates';
import {
  createTranslationRegionGroup,
  editTranslationRegionGroup,
  EditTranslationRegionGroupData,
  TranslationRegionModificationType,
  UpdateRegionModification,
} from 'molbio/dao/SequenceDAO';
import {modelEndToDisplay, modelStartToDisplay} from 'molbio/SeqUtil';
import Translation from 'molbio/translations/backbone/Translation';
import {
  confirmTranslationLinkInvalidationRisk,
  getTranslationRegionGroupsForSequence,
  TranslationRegionGroupData,
  updateModelsAfterTranslationChange,
} from 'molbio/translations/TranslationUtil';
import notifications from 'notifications';
import {getStore} from 'redux-stores/Store';
import {cancelCreateNewTranslation} from 'toolbar/feature-panel/FeaturePanelUIReduxActions';
import sortedBy from 'util/sortedBy';

import {TranslationPanelContentProps} from './TranslationPanelContent';

export interface CreateNewTranslationData {
  name: string;
  notes: string;
  strand: Strand;
  start: number;
  end: number;
  customFields: Array<CustomField>;
  codonTranslationTable: CodonTranslationTable;
  coerceStartCodonToMethionine: boolean;
  color: string;
}

export function translationPositionToString(
  group: TranslationRegionGroupData,
  sequenceStartIndex: number,
  sequenceLength: number
): string {
  return group.translations
    .map(
      ({start, end}) =>
        `${modelStartToDisplay(start, sequenceStartIndex)}-${modelEndToDisplay(
          end,
          sequenceLength,
          sequenceStartIndex
        )}`
    )
    .join(', ');
}

/**
 * Return props for the translations panel, or null if translations are not applicable.
 */
export function getTranslationPanelProps(
  sequence: Sequence,
  panelState: FeaturePanelState
): TranslationPanelContentProps | null {
  if (!sequence.supportsTranslations()) {
    return null;
  }
  const translationsByID: {[id: number]: Translation} = groupByUniqueKey(
    sequence.translations().models,
    (model) => model.id
  );
  const translationRegionGroups = getTranslationRegionGroupsForSequence(sequence);
  const translationRegionGroupsByCid: {
    [cid: string]: TranslationRegionGroupData;
  } = groupByUniqueKey(translationRegionGroups, (model) => model.cid);

  return {
    sortedTranslationRegionGroups: sortedBy(translationRegionGroups, [
      (group) => group.translations[0].start,
    ]),
    canEditSequenceFeatures: sequence.allowFeatureEdits(),
    sequenceId: sequence.id,
    sequenceLength: sequence.length(),
    sequenceStartIndex: sequence.startIndex(),
    sequenceBases: sequence.bases(),
    nucleotideType: sequence.nucleotideType(),
    isSingleStranded: sequence.isSingleStranded(),
    isCircularSequence: sequence.circular(),
    onSelectTranslation: (groupCid: string, selected: boolean) => {
      const group = translationRegionGroupsByCid[groupCid];
      if (selected) {
        const firstRegion = translationsByID[group.translations[0].id];
        sequence.selection().select(firstRegion);
        sequence.trigger('jump', firstRegion.start());
      } else {
        for (const translation of group.translations) {
          const translationModel = translationsByID[translation.id];
          translationModel.set({selected: false});
        }
      }
    },
    deleteTranslationRegionGroup: async (cid: string) => {
      const translationRegionGroup = translationRegionGroupsByCid[cid];
      if (
        !(await confirmTranslationLinkInvalidationRisk(
          sequence.tagSchema(),
          translationRegionGroup.codonTranslationTable !== CodonTranslationTable.STANDARD,
          translationRegionGroup.coerceStartCodonToMethionine,
          true,
          1
        ))
      ) {
        return;
      }
      if (translationRegionGroup.hasGroup) {
        sequence.deleteTranslationGroup(parseInt(cid, 10));
      } else {
        const translation = translationsByID[translationRegionGroup.translations[0].id];
        sequence.deleteFeature(translation);
      }
      notifications.success('Successfully deleted translation');
      analytics.track('translation', 'deleted_translations', {
        product: ANALYTICS_MODULE.MOLBIO,
        sequence_id: sequence.id,
        source: 'translations-panel',
        translation_ids: translationRegionGroup.translations.map(({id}) => id),
      });
    },
    editTranslationRegionGroup: async (cid: string, data: EditTranslationRegionGroupData) => {
      const {hasGroup, translations} = translationRegionGroupsByCid[cid];
      let res;
      if (hasGroup) {
        res = await editTranslationRegionGroup(sequence.id, parseInt(cid, 10), data);
      } else {
        // If we do not already have an update spec for the lone translation, add one
        const existingTranslationUpdate: UpdateRegionModification = {
          modificationType: TranslationRegionModificationType.UPDATE,
          id: translations[0].id,
        };
        const updateRegions = data.regions.filter(
          ({modificationType}) => modificationType === TranslationRegionModificationType.UPDATE
        );
        const dataWithExisting = {
          ...data,
          regions: updateRegions.length > 0 ? data.regions : [...data.regions, existingTranslationUpdate],
        };
        res = await createTranslationRegionGroup(sequence.id, dataWithExisting);
      }
      const {translationGroup, translationRegions} = res;
      const translationsToDeselect = translations.map(({id}) => translationsByID[id]);
      updateModelsAfterTranslationChange(
        sequence,
        [translationGroup],
        translationRegions,
        translationsToDeselect
      );
      getStore().dispatch(cancelCreateNewTranslation(sequence.id));
      notifications.success(`Successfully saved translation ${translationGroup.name}`);
      analytics.track('translation', 'updated_translations', {
        product: ANALYTICS_MODULE.MOLBIO,
        sequence_id: sequence.id,
        source: 'translations-panel',
        translation_group_ids: [translationGroup.id],
        translation_ids: translationRegions.map(({id}) => id),
      });
    },
    createNewTranslationRegionGroup: async (data: CreateNewTranslationData) => {
      const {translationGroup, translationRegions} = await createTranslationRegionGroup(sequence.id, {
        properties: {
          name: data.name,
          notes: data.notes,
          customFields: data.customFields,
          codonTranslationTable: data.codonTranslationTable,
          coerceStartCodonToMethionine: data.coerceStartCodonToMethionine,
          color: data.color,
        },
        regions: [
          {
            modificationType: TranslationRegionModificationType.CREATE,
            start: data.start,
            end: data.end,
            strand: data.strand,
          },
        ],
      });
      updateModelsAfterTranslationChange(sequence, [translationGroup], translationRegions, []);
      getStore().dispatch(cancelCreateNewTranslation(sequence.id));
      notifications.success(`Successfully saved translation ${translationGroup.name}`);
      analytics.track('translation', 'created_translation', {
        product: ANALYTICS_MODULE.MOLBIO,
        sequence_id: sequence.id,
        source: 'translations-panel',
        translation_group_ids: [translationGroup.id],
        translation_ids: translationRegions.map(({id}) => id),
      });
    },
    openExpandedTranslations: () => {
      (sequence.fileState() as SequenceFileState).openTranslationTabView();
      panelState.trigger('close');
    },
    tagSchema: sequence.tagSchema(),
  };
}
